import React, { useState, useEffect } from 'react';
import { getAccessToken } from './ec-landing-repo';
import { Alert, Box, Snackbar } from '@mui/material';
import { refreshToken } from '../../common/refresh-token-repo';
import { getConfig } from '../../common/env-config';
import ECLandingLocation from './ec-landing-location';
import { ECLocationBoxStyle } from '../../../mui/website-mui-style';

function ECSignupForm() {
    const { baseUrl } = getConfig();
    const [location, setLocation] = useState(null);
    const [externalLead, setExternalLead] = useState(() => {
        const savedFormData = localStorage.getItem('ecSignupForm');
        return savedFormData
            ? JSON.parse(savedFormData)
            : {
                fullName: '',
                contactNumber: '',
                contactEmail: '',
                companyName: '',
                leadLocation: null,
                placeId: null,
                newNote: '',
                termsCondition: false,
            };
    });
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [mobileNumberError, setMobileNumberError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [openErrorAlert, setOpenErrorAlert] = useState(false);
    const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

    useEffect(() => {
        const route = window.location.pathname;
        const parts = route.split('/');

        if (parts.length === 5 && parts[2] === 'signup') {
            const email = parts[3];
            const ecleadsource = parts[4];
            if (email && ecleadsource) {
                localStorage.setItem('email', email);
                localStorage.setItem('ecleadsource', ecleadsource);
                window.history.replaceState(null, '', '/ec/signup/for-businesses');
            }
        } else {
            let agentId = localStorage.getItem('email');
            const adId = parts[4];

            if (!agentId) {
                agentId = 'externalagent@gmail.com';
                localStorage.setItem('email', agentId);
            }

            if (adId) {
                localStorage.setItem("ecleadsource", adId);
            } else {
                console.warn("adId is undefined. Setting default value for 'ecleadsource'");
                localStorage.setItem("ecleadsource", "Organic");
            }

            getAccessToken(agentId);
        }
    }, []);

    const handleChange = (fieldName, value) => {
        if (fieldName === 'contactEmail') setEmailError("");
        if (fieldName === 'contactNumber') setMobileNumberError("");
        setExternalLead((prevState) => {
            const updatedLead = {
                ...prevState,
                [fieldName]: value,
            };

            localStorage.setItem('ecSignupForm', JSON.stringify(updatedLead));
            sessionStorage.setItem('ecSessionFormData', JSON.stringify(updatedLead));

            return updatedLead;
        });
        setIsFormDirty(true);
    };

    const handleSuccessAlert = () => {
        setOpenSuccessAlert(true);
    };

    const handleSuccessAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenSuccessAlert(false);
    };

    const handleSave = async () => {
        try {
            setIsFormDirty(false);
            const { fullName, contactNumber, contactEmail, companyName, leadLocation, placeId, newNote } = externalLead;

            const response = await fetch(`${baseUrl}/internal/ec-leads/save-external`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem("accessToken")}`
                },
                body: JSON.stringify({
                    fullName, contactNumber, contactEmail, companyName, leadLocation, placeId, newNote,
                    source: localStorage.getItem("ecleadsource")
                })
            });

            const responseBody = await response.json();

            if (!response.ok) {
                if (responseBody.message?.includes("Mobile")) {
                    setMobileNumberError("Mobile number already exist.");
                    setEmailError("");
                } else if (responseBody.message?.includes("Email")) {
                    setEmailError("Email ID already exist. Please use a different email or log in.");
                    setMobileNumberError("");
                } else if (responseBody.message?.includes("User")) {
                    setMobileNumberError("Mobile number already exist.");
                    setEmailError("Email ID already exist. Please use a different email or log in.");
                } else if (response.status === 499) {
                    refreshToken();
                } else {
                    handleErrorAlert();
                    console.error('Error saving external ec lead details:', responseBody.message);
                }
            } else {
                localStorage.removeItem('ecSignupForm');
                sessionStorage.removeItem('ecSessionFormData');
                handleSuccessAlert();
                setLocation(null);
                setExternalLead({
                    fullName: '', contactNumber: '', contactEmail: '', companyName: '',
                    leadLocation: null, placeId: null, newNote: '', termsCondition: false
                });
                setMobileNumberError("");
                setEmailError("");

                window.location.href = "https://outlook.office365.com/owa/calendar/ProductDemo@ttlmedia.in/bookings/";
            }
        } catch (error) {
            handleErrorAlert();
            console.error('Error saving external ec lead details:', error);
        }
    };

    useEffect(() => {
        if (isFormDirty) {
            const handleBeforeUnload = (e) => {
                const currentPath = window.location.pathname;
                if (isFormDirty && currentPath.includes("/ec")) {
                    const message = 'You have unsaved changes. Are you sure you want to leave?';
                    e.returnValue = message;
                    return message;
                }
            };

            const handleNavClick = (e) => {
                if (isFormDirty) {
                    const message = 'You have unsaved changes. Are you sure you want to leave?';
                    if (!window.confirm(message)) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                    } else {
                        setIsFormDirty(false);
                    }
                }
            };

            const handlePopState = (e) => {
                const currentPath = window.location.pathname;
                if (isFormDirty && currentPath.includes("/ec")) {
                    const message = 'You have unsaved changes. Are you sure you want to leave?';
                    const userConfirmed = window.confirm(message);
                    if (!userConfirmed) {
                        window.history.pushState(null, '', window.location.pathname);
                    } else {
                        setIsFormDirty(false);
                    }
                }
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            window.addEventListener('popstate', handlePopState);
            window.history.pushState(null, '', window.location.pathname);

            const navLinks = document.querySelectorAll('.nav-link');
            navLinks.forEach((link) => {
                link.addEventListener('click', handleNavClick);
            });

            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                window.removeEventListener('popstate', handlePopState);
                navLinks.forEach((link) => {
                    link.removeEventListener('click', handleNavClick);
                });
            };
        }
    }, [isFormDirty]);

    const disable = externalLead.fullName === "" || externalLead.fullName.length < 2 ||
        externalLead.contactNumber === "" || !/^[6-9]\d{9}$/.test(externalLead.contactNumber) ||
        externalLead.contactNumber.length < 10 ||
        externalLead.contactEmail === "" || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(externalLead.contactEmail) ||
        externalLead.companyName === "" || externalLead.companyName.length < 2 ||
        externalLead.termsCondition === false || externalLead.newNote === "";

    const handleErrorAlert = () => {
        setOpenErrorAlert(true);
    };

    const handleErrorAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenErrorAlert(false);
    };

    return (
        <div className='flex justify-items-end max-xs:items-center max-xs:flex-col'>
            <div className="bg-white rounded-3xl p-6 xl:p-8 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.1)] w-[550px] h-[630px] xl:w-[628px] xl:h-[720px]  max-xs:w-[88%] max-xs:flex-col max-xs:h-auto">
                {/* <div className=" flex justify-center items-center">
                    <span className='font-sans text-20 xl:text-30 text-primary-500 font-semibold  items-center h-[27px] xl:h-10 leading-[27.24px] xl:leading-[40.85px]'>Sign Up</span>
                </div> */}
                <div className='flex flex-col gap-5 xl:gap-4 max-xs:gap-5'>
                    <div className='flex justify-center gap-5 max-xs:gap-1 pt-6 xl:pt-8 max-xs:flex-col'>
                        <div className='flex flex-col gap-2 xl:gap-3'>
                            <label className="font-sans font-normal text-14  xl:text-16 text-secondary-200 h-[19px] xl:h-[22px] leading-[19.07px] xl:leading-[21.79px]">Full Name<span className=' text-[#DE3B35] text-Required'> *</span></label>
                            <input type="text" className="border border-solid focus:outline-none rounded-lg border-secondary-700 w-[241px] xl:w-[270px] h-10 font-sans text-14 xl:text-16 font-normal px-4 text-secondary-200 placeholder-secondary-100 max-xs:w-full leading-[19.07px]  xl:leading-[21.79px]" name="fullName"
                                value={externalLead.fullName}
                                onChange={(e) => handleChange("fullName", e.target.value.replace(/[^A-Za-z\s]/g, ""))}
                                maxLength={150} placeholder="Enter your Full name"
                            />
                            <small style={{
                                color: '#FA2020', fontSize: '12px', visibility: externalLead.fullName !== "" && externalLead.fullName.length < 2 ? 'visible' : 'hidden',
                            }} className='leading-[16.34px] h-4 xl:leading-[19.07px] xl:h-[19px]'
                            >
                                Length must be at least 2 character.
                            </small>
                        </div>
                        <div>
                            <div className="flex flex-col gap-2 xl:gap-3">
                                <label className="font-sans font-normal text-14 xl:text-16 text-secondary-200 h-[19px] xl:h-[22px] leading-[19.07px] xl:leading-[21.79px]">
                                    Mobile Number
                                    <span className="text-[#DE3B35] text-Required"> *</span>
                                </label>
                                <div className="border border-solid rounded-lg border-secondary-700 w-[241px] xl:w-[270px] h-10 font-sans text-14 xl:text-16 font-normal text-secondary-200 flex items-center max-xs:w-full">
                                    <span className="font-normal text-sm p-4" id="basic-addon1 mr-2">
                                        +91
                                    </span>
                                    <div className="h-[60%] w-px bg-secondary-100 mr-3 lg:hidden xl:block"></div>
                                    <input
                                        type="text"
                                        className="border-none focus:outline-none text-secondary-200 placeholder-secondary-100 leading-[19.07px] xl:leading-[21.79px]"
                                        name="contactNumber"
                                        value={externalLead.contactNumber}
                                        onChange={(e) => handleChange("contactNumber", e.target.value.replace(/\D/g, ""))}
                                        maxLength={10}
                                        placeholder="Enter your mobile number"
                                        aria-describedby="basic-addon1"
                                    />
                                </div>
                                <small
                                    style={{
                                        color: '#FA2020', fontSize: '12px',
                                        visibility:
                                            externalLead.contactNumber !== "" &&
                                                !/^[6-9]\d{9}$/.test(externalLead.contactNumber)
                                                ? 'visible'
                                                : externalLead.contactNumber !== "" && mobileNumberError !== ""
                                                    ? 'visible' : 'hidden'
                                    }} className='leading-[16.34px] h-4  xl:leading-[19.07px] xl:h-[19px]'
                                >
                                    {externalLead.contactNumber !== "" &&
                                        !/^[6-9]\d{9}$/.test(externalLead.contactNumber)
                                        ? "Enter a valid 10-digit number"
                                        : externalLead.contactNumber !== "" && mobileNumberError !== ""
                                            ? mobileNumberError
                                            : ""}
                                </small>
                            </div>
                        </div>
                    </div>
                    <div className='flex justify-center max-xs:flex-col'>
                        <div className='flex flex-col gap-2 xl:gap-3'>
                            <label className='font-sans font-normal text-14 xl:text-16 text-secondary-200 h-[19px]  xl:h-[22px] leading-[19.07px] xl:leading-[21.79px]'>Email Address<span className=' text-[#DE3B35] text-Required'> *</span></label>
                            <input type="email" className="border border-solid focus:outline-none rounded-lg border-secondary-700 w-[502px] xl:w-[564px] h-10 font-sans text-14 xl:text-16 font-normal px-4 text-secondary-200 placeholder-secondary-100 max-xs:w-full leading-[19.07px] xl:leading-[21.79px]" name="contactEmail"
                                value={externalLead.contactEmail} placeholder='Enter your email address'
                                onChange={(e) => handleChange("contactEmail", e.target.value)} maxLength={100} />
                            <small
                                style={{
                                    color: '#FA2020', fontSize: '12px',
                                    visibility:
                                        externalLead.contactEmail != null &&
                                            externalLead.contactEmail !== '' &&
                                            !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                                                externalLead.contactEmail
                                            ) ? 'visible'
                                            : externalLead.contactEmail != null &&
                                                externalLead.contactEmail !== '' && emailError !== ""
                                                ? 'visible' : 'hidden',
                                }} className='leading-[16.34px] h-4  xl:leading-[19.07px] xl:h-[19px]'
                            >
                                {externalLead.contactEmail != null &&
                                    externalLead.contactEmail !== " " &&
                                    !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                                        externalLead.contactEmail
                                    ) ? 'Please enter a valid email address'
                                    : externalLead.contactEmail != null &&
                                        externalLead.contactEmail !== "" && emailError !== ""
                                        ? emailError : " "}
                            </small>
                        </div>
                    </div>
                    <div className='flex gap-5 max-xs:gap-0 max-xs:flex-col'>
                        <div className='flex flex-col gap-2 xl:gap-3'>
                            <label className='font-sans font-normal text-14 xl:text-16 text-secondary-200 h-[19px]  xl:h-[22px] leading-[19.07px] xl:leading-[21.79px]'>Brand Name<span className=' text-[#DE3B35] text-Required'> *</span></label>
                            <input type="text" className="border border-solid focus:outline-none rounded-lg border-secondary-700 w-[234px]  xl:w-[270px] h-10 font-sans text-14 xl:text-16 font-normal px-4 text-secondary-200 placeholder-secondary-100 max-xs:w-full leading-[19.07px] xl:leading-[21.79px]" name="companyName"
                                value={externalLead.companyName} maxLength={100} placeholder='Enter your brand name'
                                onChange={(e) => handleChange("companyName", e.target.value)} />
                            <small
                                style={{
                                    color: '#FA2020', fontSize: '12px',
                                    visibility:
                                        externalLead.companyName !== "" && externalLead.companyName.length < 2
                                            ? 'visible'
                                            : 'hidden',
                                }} className='leading-[16.34px] h-4  xl:leading-[19.07px] xl:h-[19px]'
                            >
                                Length must be at least 2 character.
                            </small>
                        </div>

                        <div className='flex flex-col gap-2 xl:gap-3'>
                            <label className='font-sans font-normal text-14 xl:text-16 text-secondary-200 h-[19px]  xl:h-[22px] leading-[19.07px] xl:leading-[21.79px]'>Location<span className=' text-[#DE3B35] text-Required'> *</span></label>
                            <Box sx={ECLocationBoxStyle} className="w-[234px] xl:w-[270px] h-10 max-xs:w-full leading-[19.07px]">
                                <ECLandingLocation handleChange={handleChange} locationDescription={externalLead.leadLocation} location={location} setLocation={setLocation} className="" />
                            </Box>
                        </div>
                    </div>

                    <div className='max-xs:flex-col'>
                        <div className='flex flex-col gap-2 xl:gap-3 max-xs:mt-2'>
                            <label className='font-sans font-normal text-14 xl:text-16 text-secondary-200 h-[19px]  xl:h-[22px] leading-[19.07px] xl:leading-[21.79px]'>Website / Insta Handle</label>
                            <input type="text" className="border border-solid focus:outline-none rounded-lg border-secondary-700 w-[502px] xl:w-[564px] h-10 font-sans text-14 xl:text-16 font-normal px-4 text-secondary-200 max-xs:w-full leading-[19.07px] xl:leading-[21.79px]" name="newNote"
                                value={externalLead.newNote} maxLength={100}
                                onChange={(e) => handleChange("newNote", e.target.value)} />
                            <small
                                style={{
                                    color: '#FA2020', fontSize: '12px',
                                    visibility:
                                        externalLead.newNote !== "" && externalLead.newNote.length < 2
                                            ? 'visible'
                                            : 'hidden',
                                }} className='leading-[16.34px] h-4  xl:leading-[19.07px] xl:h-[19px]'
                            >
                                Length must be at least 2 character.
                            </small>
                        </div>
                    </div>
                </div>
                <div className=' flex pt-6 xl:pt-3.5'>
                    <input type="checkbox" className="accent-[#6750A4]" name='termsCondition' checked={externalLead.termsCondition}
                        id="terms-condition" onChange={(e) => handleChange('termsCondition', !externalLead.termsCondition)}
                    />
                    <label className="font-sans text-xs  xl:text-14 font-semibold text-secondary-400 p-px pl-3 leading-[16.34px] h-4 xl:h-[19px] xl:leading-[19.07px]" htmlFor="terms-condition">
                        By clicking Submit, you agree to schedule a call.
                    </label>
                </div>
                <div className='flex justify-center items-center pt-4 xl:pt-3.5'>
                    <button type="submit" className={`${disable ? "bg-[#C5C5C5] cursor-not-allowed" : "bg-secondary-400 cursor-pointer"} text-white border border-solid rounded-3xl w-[502px] xl:w-[564px] h-10 font-semibold text-14 leading-[19.07px]`} onClick={handleSave}
                        disabled={disable}>Schedule a call</button>
                </div>
            </div>

            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={openSuccessAlert} autoHideDuration={3000} onClose={handleSuccessAlertClose}
            >
                <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
                    Call scheduled successfully!
                </Alert>
            </Snackbar>

            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={openErrorAlert} autoHideDuration={3000} onClose={handleErrorAlertClose}
            >
                <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
                    Failed To scheduled call!
                </Alert>
            </Snackbar>
        </div>
    )
}

export default ECSignupForm;